import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { FC, useMemo, useRef } from 'react';

import { getCroppedImageUrl } from '@/components/common/CroppedImage';
import { ViewportLoad } from '@/components/common/LazyLoad';
import WeeeVideo, { WeeeVideoRefProps } from '@/components/pdp/Picture/video';
import { StatusEnum } from '@/types/cart';
import { ProductCardProps } from './';
import styles from './cardHead.module.scss';
import CountDownLabel from './countDownLabel';
import Favorite from './favorite';

const CardHead: FC<Partial<ProductCardProps>> = props => {
  const {
    data,
    type,
    showLabel = true,
    showFavorite,
    preSell,
    imageSize,
    isHorizontal,
    isLightningCard,
    trackSaveClick,
    viewOptions = {},
    supportPurchaseStatusNotify,
    className,
    soldOutCls,
    isLightningDeals
  } = props;
  const supportVideo = !!props.onViewChange;
  const { t } = useTranslation('common');
  const isSoldOut = useMemo(() => {
    return data?.sold_status === StatusEnum?.sold_out;
  }, [data]);
  let labelList = data?.label_list?.length > 0 ? [data.label_list[0]] : [];
  if (!!labelList?.length && labelList?.[0]?.label_key === 'off') {
    labelList = data?.label_list?.filter(item => {
      return item.label_key === 'off';
    });
  }

  /**
   * 预售
   * preSell 老预售字段，通过pre_sell_products字段判断(首页、合集)
   * is_presale 新预售字段，在商品属性上
   * 售罄的话不展示预售
   */
  if (data?.is_presale) {
    if (isSoldOut) {
      labelList = [];
    }
  } else if (preSell) {
    labelList = [
      {
        label_color: '#820b6a',
        label_name: t('pre_sell'),
        label_position: 'title'
      }
    ];
  }

  /**
   * 商品卡片是否用在秒杀页面
   * 如果在秒杀页面，可能会有展示上下两个label的情况(双折扣)
   * 其他页面则最多展示一个label(第一个)
   * https://sayweee.atlassian.net/issues/PEP-8665
   */
  if (!isLightningDeals) {
    labelList = labelList.slice(0, 1);
  }

  const weeeVideoRef = useRef<WeeeVideoRefProps>(null);
  const cardEl = useRef<HTMLDivElement>(null);
  const videoDetail = useMemo(() => {
    return data?.media_urls?.find(item => item.media_type === 'video');
  }, [data]);

  const productImage = (
    <img
      data-role="product-image"
      className={styles.productImg}
      src={getCroppedImageUrl(data?.square_img_url || data?.img || data?.image_url, {
        renderWidth: imageSize || 170,
        devicePixelRatio: 2
      })}
      alt={`weee_${data?.parent_category}_${data?.name}`}
    />
  );

  return (
    <div
      className={classNames(styles.imgCard, { [styles.horizontalCardHead]: isHorizontal }, className)}
      data-role="image-container"
      ref={cardEl}>
      <div className={styles.innerImgWrapper} role="img">
        {videoDetail && supportVideo ? (
          <ViewportLoad
            placeholder={<div className={styles.productImg} />}
            initialLoad
            viewOptions={{
              delay: 200,
              threshold: 1,
              ...viewOptions
            }}
            onViewChange={inView => {
              props?.onViewChange?.({
                inView,
                play: () => {
                  weeeVideoRef.current?.autoPlayVideo();
                },
                stop: () => {
                  weeeVideoRef.current?.resetVideo();
                },
                productInfo: data,
                mediaItem: videoDetail,
                cardElement: cardEl.current
              });
            }}>
            <>
              <WeeeVideo
                ref={weeeVideoRef}
                poster={getCroppedImageUrl(data?.square_img_url || data?.img || data?.image_url, {
                  renderWidth: imageSize || 170,
                  devicePixelRatio: 2
                })}
                src={videoDetail.media_url}
                loop={videoDetail.autoplay === -1}
                className={styles.video}
                controlPanel={{ showMuteIcon: false, showPlayIcon: true }}
                defaultProps={{ showPlayIcon: true }}
              />
            </>
          </ViewportLoad>
        ) : props.isRobot ? (
          productImage
        ) : (
          <ViewportLoad placeholder={<div className={styles.productImg} />} className={styles.productImgWrapper}>
            {productImage}
          </ViewportLoad>
        )}
      </div>

      {props.isLightningCard && props?.countDownTimeStamp && props?.data?.status === 'ongoing' ? (
        <div className={styles.imgHeader}>
          <CountDownLabel status="ongoing" start={props?.countDownTimeStamp} end={props?.data?.end_time} />
        </div>
      ) : (
        <div className={classNames(styles.imgHeader, { [styles.isTradeInLabel]: labelList?.[0]?.tradeInLabel })}>
          {showLabel && (
            <div className={styles.labelBox}>
              {labelList?.map((label, i) => (
                <span
                  className={classNames(
                    'flex justify-center items-center h-[18px] py-1 px-2 rounded-full box-border text-pricing-fg enki-body-3xs-strong',
                    { [styles.invalidOffTag]: label.label_position === 'price_invalid' }
                  )}
                  key={`label-${i}`}
                  style={{ background: label?.label_color }}>
                  {label?.label_name}
                </span>
              ))}
            </div>
          )}

          {!isLightningCard &&
            showFavorite &&
            data?.category !== 'bundle' &&
            data?.is_hotdish !== 'Y' &&
            data?.sold_status &&
            data?.sold_status !== StatusEnum.sold_out && (
              <Favorite
                className={styles.favIcon}
                isInCard={true}
                id={data?.product_id || data?.id}
                supportPurchaseStatusNotify={supportPurchaseStatusNotify}
                onClick={trackSaveClick}
              />
            )}
        </div>
      )}

      {isSoldOut && (
        <div className={classNames(styles.soldOut, soldOutCls)} data-role="soldout">
          {t('sold_out')}
        </div>
      )}
      {props.children}
    </div>
  );
};
export default CardHead;
